import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

import { SessionGatesContext } from './SessionGatesContext';
import {
  buildPathWithoutCheckoutParams,
  CHECKOUT_SUCCESS_QUERY_PARAM,
  clearPendingSubscriptionConversion,
  clearPendingSubscriptionSuccess,
  hasPendingSubscriptionConversion,
  hasPendingSubscriptionSuccess,
  isEligibleForSubscriptionConversion,
  markPendingSubscriptionConversion,
  markPendingSubscriptionSuccess,
  subscriptionConversionId,
} from './subscriptionConversion';
import { AIDrawerContext } from '../providers/AIDrawerContext';
import { isStudyRoute } from '../os/osRouteMeta';
import { examsSelectors } from '../slices/exams/examsSlice';
import { getUserSchool } from '../services/users';
import { isAllowedPreOnboardingPath } from '../routes/ProtectedRoute';
import { selectStudyPhase, StudyPhase } from '../slices/study/studySlice';
import { getActiveImpersonation, getUserInfo } from '../services/auth';
import { getSubscriptionStatus } from '../services/subscriptions';
import { getAssentsToAccept, getLatestAssents } from '../services/assent';
import { Capacitor } from '@capacitor/core';
import {
  hasCompletedTour,
  isTipDismissed,
  markTipSeen,
  markTourCompleted,
} from '../helpers/messageStateGating';
import useWindowWidth from '../hooks/useWindowWidth';
import { useActivateExamFromUrl } from '../hooks/useActivateExamFromUrl';
import { usePrimaryExamType } from '../hooks/usePrimaryExamType';
import { isSketchyPartnershipAttribution } from '../utils/attribution';
import { reportGoogleAdsSubscriptionConversion } from '../utils/googleAds';
import { withPosthogAfterIdle } from '../utils/posthogClient';
import { scheduleDeferredTask } from '../utils/scheduleDeferredTask';
import { MINIMUM_START_DATE, didSignUpViaWebinar } from '../utils/tourHelpers';
import type { AssentType } from '../types/Assent';
import type { RootState } from '../store/store';

const AssentsModal = lazy(() => import('../components/ora/Home/AssentsModal'));
const WelcomeToOra = lazy(() => import('./WelcomeToOra'));
const SubscriptionSuccess = lazy(() => import('./SubscriptionSuccess'));
const AppPromo = lazy(() => import('./AppPromo'));
/** How often, and for how long, the subscription is re-read after checkout. */
const SUBSCRIPTION_POLL_MS = 3000;
const SUBSCRIPTION_POLL_ATTEMPTS = 20;

const MappingJobsWatcher = lazy(
  () => import('../components/ora/MappingJobsWatcher')
);

/** Matches the beat the Dashboard's tours wait before they appear. */
const WELCOME_DELAY_MS = 750;

/** The Dashboard window (`Home`), whose feature tours open with a welcome. */
const isDashboardRoute = (pathname: string): boolean =>
  pathname === '/dashboard' || pathname.startsWith('/dashboard/');

type AssentToAccept = { type: AssentType; version: string };

/**
 * Everything that used to be tied to the home page but is really about the
 * session — a student who is signed in and onboarded — rather than about any
 * one screen: policy assents, the first-run welcome, the checkout return,
 * links minted by other systems (`?activateExam=`, `?delete-request=`), the
 * app-store promo, the analytics identity, and the sign-up attribution
 * cleanup.
 *
 * Mounted once in `ProtectedLayout`, so it runs whatever renders `/` — the
 * home page has changed hands before (it is the AI desktop now, and the
 * Dashboard lives at `/dashboard`), and every time it did, whatever was bolted
 * to the old component silently stopped: the Stripe return URL still points
 * at `/`, and nothing there was listening.
 *
 * Two rules keep this from becoming a second home page:
 *
 *  - **Lift gates, keep data.** Anything that exists to render or refresh the
 *    Dashboard (assignments, session freshness, stats, mapping jobs, the
 *    anchored feature tours) stays with the Dashboard.
 *  - **Evaluate anywhere, present only when quiet.** The checks run on mount
 *    regardless of route; anything modal waits for a non-study surface (see
 *    `isQuiet`) so a policy prompt never lands on a timed block.
 *
 * Modal UI from either tree registers in `SessionGatesContext` so the two can
 * take turns: assents, then the checkout animation, then the welcome, then the
 * app promo, and only then the Dashboard's own tours and promos.
 */
export const SessionGates = ({ children }: { children: ReactNode }) => {
  const [blockingIds, setBlockingIds] = useState<readonly string[]>([]);
  const [shownIds, setShownIds] = useState<readonly string[]>([]);

  const release = useCallback((id: string) => {
    setBlockingIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : ids
    );
  }, []);

  const claim = useCallback(
    (id: string) => {
      setBlockingIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
      setShownIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
      return () => release(id);
    },
    [release]
  );

  const value = useMemo(
    () => ({
      claim,
      release,
      isBlocked: blockingIds.length > 0,
      blockingIds,
      shownIds,
    }),
    [blockingIds, claim, release, shownIds]
  );

  const currentUser = useSelector((state: RootState) => state.user.profile);
  const location = useLocation();
  const navigate = useNavigate();

  // The checkout return is captured on ANY route, onboarding included: a new
  // student can come back from Stripe before onboarding is done, and the
  // redirect there used to drop the query. What it means is remembered in
  // sessionStorage — the conversion to report, the animation to play — and
  // the params come off the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(CHECKOUT_SUCCESS_QUERY_PARAM) !== 'true') return;

    markPendingSubscriptionConversion();
    markPendingSubscriptionSuccess();

    const sanitizedPath = buildPathWithoutCheckoutParams(location);
    if (
      sanitizedPath !== `${location.pathname}${location.search}${location.hash}`
    ) {
      navigate(sanitizedPath, { replace: true });
    }
  }, [location, navigate]);

  return (
    <SessionGatesContext.Provider value={value}>
      {/* Mounted only once the student is onboarded: onboarding itself, and
          the pre-onboarding routes, render inside the protected layout too. */}
      {currentUser?.isOnboarded === true ? <ActiveSessionGates /> : null}
      {children}
    </SessionGatesContext.Provider>
  );
};

/** Registers `id` in the session-gate register while `isShown`. */
const useClaimWhile = (id: string, isShown: boolean) => {
  const { claim } = useContext(SessionGatesContext);

  useEffect(() => {
    if (!isShown) return;
    return claim(id);
  }, [claim, id, isShown]);
};

const ActiveSessionGates = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const subscriptionDetails = useSelector(
    (state: RootState) => state.subscription.subscription
  );

  const studyPhase = useSelector(selectStudyPhase);
  const aiDrawer = useContext(AIDrawerContext);
  const { primaryExam } = usePrimaryExamType();
  const activeCustomExam = useSelector(examsSelectors.selectActiveCustomExam);
  const location = useLocation();
  const navigate = useNavigate();

  // Shared-exam notifications link to `/?activateExam=<id>`.
  useActivateExamFromUrl();

  /**
   * A surface where a modal may land: no study session in progress, no
   * fullscreen game round (which stands the AI bubble down the same way),
   * and none of the routes that render before onboarding is finished. The
   * study route is checked by path as well as by phase: the phase follows
   * the route an effect later, and a cold study deep link must not paint a
   * gate for that one render.
   */
  const isQuiet =
    studyPhase === StudyPhase.HOME &&
    !isStudyRoute(location.pathname) &&
    !(aiDrawer?.isPageSuppressed ?? false) &&
    !isAllowedPreOnboardingPath(location.pathname);

  // ---- Session setup: sign-up attribution has been consumed by now. ----
  useEffect(() => {
    sessionStorage.removeItem('ora-refId');
    sessionStorage.removeItem('ora-cohort');
  }, []);

  // ---- A fresh profile at the start of the session. The persisted copy is
  // what the app boots on, and it is only refreshed incidentally (a settings
  // write, the Account page); the gates below and the landing decision in
  // the shell read settings and tip states from it, so they wait for this
  // read (`session-gates-pending`) rather than trusting last session's. ----
  const [hasRefreshedProfile, setHasRefreshedProfile] = useState(false);

  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;

    let isCancelled = false;

    // Shares the route guard's boot fetch when it just ran.
    void getUserInfo({ reuseRecent: true }).finally(() => {
      if (!isCancelled) setHasRefreshedProfile(true);
    });

    return () => {
      isCancelled = true;
    };
  }, [currentUser?.id]);

  // ---- The student's school, for whatever asks (the rail's ambassador
  // entry, analytics). It used to load with the Dashboard, which a desktop
  // student may not open in a session. ----
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;

    return scheduleDeferredTask(() => {
      void getUserSchool(userId);
    });
  }, [currentUser?.id]);

  // ---- Document mapping jobs: polled for as long as the session lasts, so
  // a preclinical student who closes the Dashboard mid-job still hears when
  // it finishes. ----
  const mappingExamId =
    activeCustomExam?.id ||
    (primaryExam?.type === 'CUSTOM' ? primaryExam.id : undefined);

  // ---- Links minted outside the app. ----
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('delete-request') === 'true') {
      navigate('/account');
    }
  }, [location, navigate]);

  // The checkout return animation, owed since `SessionGates` captured the
  // return (possibly on an earlier route, or before onboarding was done).
  const [isSubscriptionSuccessPending, setIsSubscriptionSuccessPending] =
    useState(hasPendingSubscriptionSuccess);

  useEffect(() => {
    if (hasPendingSubscriptionSuccess()) setIsSubscriptionSuccessPending(true);
  }, [location]);

  // The conversion is reported once the subscription has actually landed —
  // the return from Stripe can beat the webhook that activates it, so while
  // one is owed and the plan is not there yet, the subscription is re-read
  // every few seconds for a minute.
  const isConversionEligible =
    isEligibleForSubscriptionConversion(subscriptionDetails);

  const conversionId = subscriptionConversionId(subscriptionDetails);

  useEffect(() => {
    if (!hasPendingSubscriptionConversion() || !isConversionEligible) return;

    void reportGoogleAdsSubscriptionConversion({
      subscriptionId: conversionId,
    }).then((conversionResult) => {
      if (conversionResult === 'reported' || conversionResult === 'duplicate') {
        clearPendingSubscriptionConversion();
      }
    });
  }, [conversionId, isConversionEligible]);

  useEffect(() => {
    if (!hasPendingSubscriptionConversion() || isConversionEligible) return;

    let attempts = 0;
    void getSubscriptionStatus();

    const interval = window.setInterval(() => {
      attempts += 1;
      if (attempts >= SUBSCRIPTION_POLL_ATTEMPTS) {
        window.clearInterval(interval);
        return;
      }
      void getSubscriptionStatus();
    }, SUBSCRIPTION_POLL_MS);

    return () => window.clearInterval(interval);
  }, [isConversionEligible]);

  // ---- Analytics identity, refreshed when the primary exam changes. Not
  // while an admin impersonates: their events are not the student's
  // (ORA-1089). ----
  useEffect(() => {
    if (!currentUser || getActiveImpersonation()) return;

    return withPosthogAfterIdle((posthog) => {
      posthog.identify(currentUser.id, {
        primaryExam: primaryExam?.name,
        isUsingFlashcards: primaryExam?.isUsingFlashcards,
        isUsingQbank: primaryExam?.isUsingQbank,
        isUsingVideos: primaryExam?.isUsingVideos,
        selectedFlashcardCount: primaryExam?.selectedFlashcardCount?.toString(),
        selectedVignetteCount: primaryExam?.selectedVignetteCount?.toString(),
        completedExamFlashcards:
          primaryExam?.flashcards?.completed?.toString() +
          ' of ' +
          primaryExam?.flashcards?.total?.toString(),
        completedExamVignettes:
          primaryExam?.vignettes?.studied?.toString() +
          ' of ' +
          primaryExam?.vignettes?.total?.toString(),
        completedExamVideos:
          primaryExam?.videos?.watched?.toString() +
          ' of ' +
          primaryExam?.videos?.total?.toString(),
        first_partner_source: currentUser.attributionSource,
        first_utm_campaign: currentUser.attributionCampaign,
        sketchy_discount_eligible: isSketchyPartnershipAttribution({
          utmCampaign: currentUser.attributionCampaign,
        }),
      });
    });
    // Re-identify on a new user or a new primary exam, not on every profile
    // field (the exam's own counts change constantly).
  }, [primaryExam?.id, currentUser?.id]);

  // ---- Policy assents. ----
  const [assentsToAccept, setAssentsToAccept] = useState<AssentToAccept[]>([]);
  const [hasCheckedAssents, setHasCheckedAssents] = useState(false);

  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;

    // Not while an admin is impersonating: the admin shouldn't be prompted to
    // accept policies on the student's behalf.
    if (getActiveImpersonation()) {
      setHasCheckedAssents(true);
      return;
    }

    let isCancelled = false;

    const cancelDeferredLoad = scheduleDeferredTask(() => {
      void getLatestAssents(userId)
        .then((latestAssents) => {
          if (isCancelled) return;
          if (latestAssents) {
            setAssentsToAccept(getAssentsToAccept(latestAssents));
          } else {
            console.error('Failed to fetch latest assents.');
          }
        })
        .finally(() => {
          if (!isCancelled) setHasCheckedAssents(true);
        });
    });

    return () => {
      isCancelled = true;
      cancelDeferredLoad();
    };
  }, [currentUser?.id]);

  // Policies are not a courtesy: a student who only ever deep-links into a
  // study session is asked there too. Only a fullscreen game round and the
  // pre-onboarding routes hold it back.
  const isAssentSurface =
    !(aiDrawer?.isPageSuppressed ?? false) &&
    !isAllowedPreOnboardingPath(location.pathname);

  const showAssents = isAssentSurface && assentsToAccept.length > 0;
  useClaimWhile('assents', showAssents);

  // ---- Checkout return: the animation waits behind due policies. ----
  const showSubscriptionSuccess =
    isQuiet && isSubscriptionSuccessPending && !showAssents;

  useClaimWhile('checkout-success', showSubscriptionSuccess);

  // ---- First-run welcome. ----
  const hasSeenWelcome = useSelector(
    (state: RootState) =>
      hasCompletedTour(state, state.user.profile, 'welcome-to-ora') ||
      // It was step one of these tours until ORA-699 split it out, so anyone
      // who finished one has seen it.
      hasCompletedTour(state, state.user.profile, 'nbme-tour-part-1') ||
      hasCompletedTour(state, state.user.profile, 'preclinical-tour-part-1') ||
      hasCompletedTour(
        state,
        state.user.profile,
        'preclinical-empty-tour-part-1'
      )
  );

  // The same audience the Dashboard tours have always had.
  const isWelcomeDue =
    !!currentUser &&
    !hasSeenWelcome &&
    !didSignUpViaWebinar(currentUser) &&
    !currentUser.ambassador &&
    !!currentUser.onboardingTips &&
    !!currentUser.createdAt &&
    moment.utc(currentUser.createdAt).isAfter(MINIMUM_START_DATE) &&
    !!primaryExam?.id;

  // Once on screen it stays until closed: marking it started updates the
  // message state, which would otherwise flip `hasSeenWelcome` under it.
  const [welcomeStage, setWelcomeStage] = useState<
    'waiting' | 'showing' | 'done' | 'skipped'
  >('waiting');

  const welcomeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The Dashboard has a welcome of its own — the first step of its feature
  // tours — so a student who lands there (the default; ORA-753) must not get
  // this one first and the tour's right after. On the Dashboard it counts as
  // seen instead, now and on later visits to the desktop.
  const isOnDashboard = isDashboardRoute(location.pathname);

  useEffect(() => {
    if (
      welcomeStage !== 'waiting' ||
      !isWelcomeDue ||
      !isOnDashboard ||
      !hasRefreshedProfile
    ) {
      return;
    }

    setWelcomeStage('skipped');
    void markTourCompleted('welcome-to-ora');
  }, [welcomeStage, isWelcomeDue, isOnDashboard, hasRefreshedProfile]);

  const isWelcomeReady =
    welcomeStage === 'waiting' &&
    isWelcomeDue &&
    isQuiet &&
    !isOnDashboard &&
    hasRefreshedProfile &&
    hasCheckedAssents &&
    assentsToAccept.length === 0 &&
    !isSubscriptionSuccessPending;

  useEffect(() => {
    if (!isWelcomeReady) return;

    welcomeTimerRef.current = setTimeout(() => {
      setWelcomeStage('showing');
    }, WELCOME_DELAY_MS);

    return () => {
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
    };
  }, [isWelcomeReady]);

  const showWelcome = welcomeStage === 'showing';
  useClaimWhile('welcome-to-ora', showWelcome);

  // While the gates are still deciding — the assents request in flight, or
  // a due welcome waiting for its beat — nothing downstream may go first:
  // the Dashboard's tour would otherwise open in the gap and, by marking
  // itself started, count as the welcome having been seen.
  useClaimWhile(
    'session-gates-pending',
    !hasRefreshedProfile ||
      !hasCheckedAssents ||
      (isQuiet && welcomeStage === 'waiting' && isWelcomeDue)
  );

  const closeWelcome = useCallback(() => setWelcomeStage('done'), []);
  // ---- App promo: the store QR card, once the intro has been seen. ----
  const { shownIds } = useContext(SessionGatesContext);
  const windowWidth = useWindowWidth();

  const hasDismissedAppPromo = useSelector((state: RootState) =>
    isTipDismissed(state, state.user.profile, 'app-promo')
  );

  const [isAppPromoDismissed, setIsAppPromoDismissed] = useState(false);
  const [isAppPromoQrOpen, setIsAppPromoQrOpen] = useState(false);

  // Not in the same session as the welcome or a Dashboard tour: the student
  // has had one thing to read already. A welcome skipped for the Dashboard
  // counts too — once it is on record as seen, the promo would otherwise be
  // eligible in the beat before the Dashboard's tour claims its turn.
  const hasHadAttentionThisSession =
    welcomeStage === 'skipped' ||
    shownIds.some((id) => ['welcome-to-ora', 'dashboard-tour'].includes(id));

  const showAppPromo =
    !!currentUser &&
    isQuiet &&
    hasRefreshedProfile &&
    !isAppPromoDismissed &&
    !hasDismissedAppPromo &&
    hasSeenWelcome &&
    !hasHadAttentionThisSession &&
    !showAssents &&
    !isSubscriptionSuccessPending &&
    !(currentUser.hasIOSApp || currentUser.hasAndroidApp) &&
    !Capacitor.isNativePlatform() &&
    windowWidth > 900;

  useClaimWhile('app-promo', showAppPromo || isAppPromoQrOpen);

  const dismissAppPromo = useCallback(() => {
    setIsAppPromoDismissed(true);
    void markTipSeen('app-promo');
  }, []);

  const finishSubscriptionSuccess = useCallback(() => {
    clearPendingSubscriptionSuccess();
    setIsSubscriptionSuccessPending(false);
  }, []);

  return (
    <>
      {mappingExamId ? (
        <Suspense fallback={null}>
          <MappingJobsWatcher examId={mappingExamId} pendingOnly />
        </Suspense>
      ) : null}
      {showAssents && currentUser ? (
        <Suspense fallback={null}>
          <AssentsModal
            currentUser={currentUser}
            assentsToAccept={assentsToAccept}
            setAssentsToAccept={setAssentsToAccept}
          />
        </Suspense>
      ) : null}
      {showSubscriptionSuccess ? (
        <Suspense fallback={null}>
          <SubscriptionSuccess onFinished={finishSubscriptionSuccess} />
        </Suspense>
      ) : null}
      {showWelcome ? (
        <Suspense fallback={null}>
          <WelcomeToOra onClose={closeWelcome} />
        </Suspense>
      ) : null}
      {showAppPromo ? (
        <Suspense fallback={null}>
          <AppPromo
            onDismiss={dismissAppPromo}
            onQrModalChange={setIsAppPromoQrOpen}
          />
        </Suspense>
      ) : null}
    </>
  );
};

export default SessionGates;
